<script setup lang="ts">
import { ChartjsRadar } from '~/utils/chartjs'

interface PolarBucketEntry { target_speed_ms: number, samples: number }

const props = defineProps<{
  polar: Record<string, Record<string, PolarBucketEntry>>,
}>()

const { convertSpeed, unitLabel } = useSpeedUnit()

// One hue (blue), light->dark by wind speed -- the reference dataviz palette's sequential ramp
// (steps 100/150/200/300/400/500/600/700 -- the two lightest buckets sit closer together since
// step 100 is the palette's lightest defined step), fixed per knot value so 10kn always reads as
// the same color regardless of which other buckets a given boat happens to have data for.
const BUCKET_COLORS: Record<number, string> = {
  4: '#cde2fb',
  6: '#b7d3f6',
  8: '#9ec5f4',
  10: '#6da7ec',
  12: '#3987e5',
  14: '#256abf',
  16: '#184f95',
  20: '#0d366b',
}
const FALLBACK_COLOR = '#898781'

// Activities with no wind speed estimate land in an "unknown" bucket server-side (see
// generate_boat_polar) -- not a usable "target speed by wind strength" curve, so it's dropped here
// rather than plotted.
const buckets = computed(() => {
  const numeric = Object.keys(props.polar ?? {}).filter(k => k !== 'unknown').map(Number).sort((a, b) => a - b)
  return numeric.map(kn => ({ key: String(kn), label: `${kn} kn`, color: BUCKET_COLORS[kn] ?? FALLBACK_COLOR }))
})

const ANGLE_STEP = 10
// Spokes at every 10deg around the full circle, 0 at top (head to wind) going clockwise through
// starboard to dead downwind (180) and back round through port -- same convention as the per-session
// polar chart on the sail-analysis page, so the two read the same way.
const spokeAngles = computed(() => {
  const angles: number[] = []
  for (let a = 0; a < 360; a += ANGLE_STEP) angles.push(a)
  return angles
})

// BoatPolar.data is unsigned/symmetric (0..180deg), mirrored to both sides for display.
const targetAt = (bucketKey: string, spokeDeg: number): number | null => {
  const unsignedKey = spokeDeg <= 180 ? spokeDeg : 360 - spokeDeg
  const entry = props.polar?.[bucketKey]?.[String(unsignedKey)]
  return entry?.target_speed_ms != null ? convertSpeed(entry.target_speed_ms) : null
}

const hasData = computed(() => buckets.value.length > 0)

const chartData = computed(() => ({
  labels: spokeAngles.value.map(a => `${a}°`),
  datasets: buckets.value.map(b => ({
    label: b.label,
    data: spokeAngles.value.map(a => targetAt(b.key, a)),
    borderColor: b.color,
    backgroundColor: b.color,
    borderWidth: 2,
    fill: false,
    pointRadius: 2,
    pointBackgroundColor: b.color,
    pointBorderColor: b.color,
    spanGaps: true,
  })),
}))

const chartOptions = computed(() => ({
  responsive: true,
  // Chart.js defaults radar charts to maintainAspectRatio: true with aspectRatio 1, which forces
  // the canvas's height to track its container's *width* -- on a wide viewport that blows the
  // chart up into a huge square. Size it from the wrapper div's CSS instead.
  maintainAspectRatio: false,
  scales: {
    r: {
      angleLines: { display: true },
      startAngle: 0,
      ticks: { callback: (value: number) => value.toFixed(1) },
      pointLabels: {
        callback: (label: string) => {
          // Only label every 30deg to avoid a cluttered ring of text
          const deg = parseInt(label, 10)
          return deg % 30 === 0 ? label : ''
        },
      },
    },
  },
  plugins: {
    // Clicking a legend entry isolates that wind-speed curve (Chart.js's default legend-click
    // toggle) -- the intended way to go from "all buckets at once" to a single one.
    legend: { display: true, position: 'bottom' as const },
    tooltip: {
      callbacks: {
        label: (item: any) => item.parsed.r != null ? `${item.dataset.label}: ${item.parsed.r.toFixed(1)} ${unitLabel.value}` : '',
      },
    },
  },
}))
</script>

<template>
  <div v-if="hasData" class="mx-auto" style="max-width: 520px; height: 440px">
    <ChartjsRadar :data="chartData" :options="chartOptions" />
  </div>
</template>
